import axios, { type AxiosError } from "axios";
import { apiClient } from "./client";

/**
 * Auth client (T01). Talks to the real backend `/api/v1/auth/*`.
 *
 * Login is clinic-code-free: the backend derives the tenant from the identifier
 * (email hoặc tên đăng nhập) + password, then returns the `tenantId` in the
 * response. We use a bare axios instance because the shared `apiClient` injects
 * a tenant header from localStorage, which isn't set yet at login time.
 */

const authClient = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api",
  timeout: 20_000,
});

const TENANT_KEY = "addy.tenantId";
const ACCESS_KEY = "addy.accessToken";
const REFRESH_KEY = "addy.refreshToken";

export interface LoginTokens {
  kind: "tokens";
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: string;
  userId: string;
  tenantId: string;
  /** False for a freshly self-registered owner who hasn't clicked the activation e-mail yet. */
  emailVerified: boolean;
}

export interface MfaChallenge {
  kind: "mfa";
  mfaToken: string;
}

export type LoginResult = LoginTokens | MfaChallenge;

export interface LoginInput {
  /** Email hoặc tên đăng nhập — the tenant is derived from this + password. */
  username: string;
  password: string;
}

/** Persist the session so `apiClient` attaches the tenant + bearer headers. */
export function storeSession(t: LoginTokens): void {
  localStorage.setItem(TENANT_KEY, t.tenantId);
  localStorage.setItem(ACCESS_KEY, t.accessToken);
  localStorage.setItem(REFRESH_KEY, t.refreshToken);
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  // tenantId is kept so the next login pre-fills the clinic field.
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem(ACCESS_KEY);
}

/**
 * Step 1 login. Resolves to tokens (MFA off) or an MFA challenge (MFA on,
 * HTTP 202). Throws a `{ message }` on failure with a Vietnamese message.
 */
export async function login(input: LoginInput): Promise<LoginResult> {
  try {
    const res = await authClient.post("/v1/auth/login", {
      username: input.username,
      password: input.password,
    });
    if (res.status === 202 || res.data?.status === "mfa_required") {
      return { kind: "mfa", mfaToken: res.data.mfaToken };
    }
    const d = res.data;
    return {
      kind: "tokens",
      accessToken: d.accessToken,
      refreshToken: d.refreshToken,
      accessExpiresAt: d.accessExpiresAt,
      userId: d.userId,
      tenantId: d.tenantId,
      // Missing on older responses / non-owner flows → treat as verified (no banner).
      emailVerified: d.emailVerified ?? true,
    };
  } catch (e) {
    throw new Error(loginErrorMessage(e as AxiosError));
  }
}

export interface RegisterInput {
  clinicName: string;
  ownerEmail: string;
  ownerPassword: string;
}

/**
 * Public self-registration (auto-approved). Creates a new clinic + owner account and returns a
 * session, so the caller is logged straight in. Throws a Vietnamese message on failure.
 */
export async function register(input: RegisterInput): Promise<LoginTokens> {
  try {
    const res = await authClient.post("/v1/auth/register", input);
    const d = res.data;
    return {
      kind: "tokens",
      accessToken: d.accessToken,
      refreshToken: d.refreshToken,
      accessExpiresAt: d.accessExpiresAt,
      userId: d.userId,
      tenantId: d.tenantId,
      // Missing on older responses / non-owner flows → treat as verified (no banner).
      emailVerified: d.emailVerified ?? true,
    };
  } catch (e) {
    throw new Error(registerErrorMessage(e as AxiosError));
  }
}

function registerErrorMessage(e: AxiosError): string {
  const status = e.response?.status ?? 0;
  const body = e.response?.data as { detail?: string; code?: string } | undefined;
  if (status === 409) return body?.detail ?? "Phòng khám này đã tồn tại.";
  if (status === 422) return body?.detail ?? "Mật khẩu không hợp lệ (có thể đã bị rò rỉ).";
  if (status === 400) return body?.detail ?? "Thông tin đăng ký không hợp lệ.";
  if (status === 0) return "Không kết nối được đến máy chủ.";
  return body?.detail ?? "Đăng ký thất bại. Vui lòng thử lại.";
}

/** Request a password-reset email. Always resolves (server never discloses if the email exists). */
export async function requestPasswordReset(email: string): Promise<void> {
  await authClient.post("/v1/auth/password/forgot", { email });
}

/** Redeem a reset token + set a new password. Throws a Vietnamese message on failure. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  try {
    await authClient.post("/v1/auth/password/reset", { token, newPassword });
  } catch (e) {
    throw new Error(actionTokenErrorMessage(e as AxiosError));
  }
}

/**
 * Confirm the e-mail address from the activation link (`/verify-email?token=…`). Public — the
 * opaque token is the credential. Throws a Vietnamese message on an invalid/expired link.
 */
export async function verifyEmail(token: string): Promise<void> {
  try {
    await authClient.post("/v1/auth/verify-email", { token });
  } catch (e) {
    throw new Error(actionTokenErrorMessage(e as AxiosError));
  }
}

/** Re-send the activation e-mail for the logged-in (still-unverified) user. Always resolves. */
export async function resendVerificationEmail(): Promise<void> {
  await apiClient.post("/v1/auth/verify-email/resend");
}

function actionTokenErrorMessage(e: AxiosError): string {
  const status = e.response?.status ?? 0;
  const body = e.response?.data as { detail?: string } | undefined;
  if (status === 401) return "Liên kết đã hết hạn hoặc không hợp lệ. Vui lòng yêu cầu lại.";
  if (status === 400) return body?.detail ?? "Mật khẩu không hợp lệ.";
  if (status === 0) return "Không kết nối được đến máy chủ.";
  return body?.detail ?? "Thao tác thất bại. Vui lòng thử lại.";
}

// ── MFA ─────────────────────────────────────────────────────────────────────

export interface MfaEnrolInfo {
  /** Base32 TOTP secret for manual entry. */
  secret: string;
  /** otpauth:// URI to render as a QR code. */
  otpAuthUri: string;
}

/** Start MFA enrolment for the logged-in user (authenticated). Returns secret + otpauth URI. */
export async function enrolMfa(): Promise<MfaEnrolInfo> {
  const res = await apiClient.post<MfaEnrolInfo>("/v1/auth/mfa/enrol");
  return { secret: res.data.secret, otpAuthUri: res.data.otpAuthUri };
}

/** Confirm MFA enrolment by proving a fresh TOTP code (authenticated). Returns recovery codes. */
export async function confirmMfaEnrol(code: string): Promise<string[]> {
  const res = await apiClient.post<{ recoveryCodes: string[] }>(
    "/v1/auth/mfa/enrol/confirm",
    { code },
  );
  return res.data.recoveryCodes ?? [];
}

/** Accept a staff invite: set full name + password, receive a session. Then route to MFA setup. */
export async function acceptInvite(
  token: string,
  fullName: string,
  password: string,
): Promise<LoginTokens> {
  try {
    const res = await authClient.post("/v1/auth/invite/accept", {
      token,
      fullName,
      password,
    });
    const d = res.data;
    return {
      kind: "tokens",
      accessToken: d.accessToken,
      refreshToken: d.refreshToken,
      accessExpiresAt: d.accessExpiresAt,
      userId: d.userId,
      tenantId: d.tenantId,
      // Missing on older responses / non-owner flows → treat as verified (no banner).
      emailVerified: d.emailVerified ?? true,
    };
  } catch (e) {
    throw new Error(actionTokenErrorMessage(e as AxiosError));
  }
}

/** Step-2 login: redeem the mfa_token + TOTP code for a full session. */
export async function verifyMfa(mfaToken: string, code: string): Promise<LoginTokens> {
  const res = await authClient.post("/v1/auth/mfa/verify", { mfaToken, code });
  const d = res.data;
  return {
    kind: "tokens",
    accessToken: d.accessToken,
    refreshToken: d.refreshToken,
    accessExpiresAt: d.accessExpiresAt,
    userId: d.userId,
    tenantId: d.tenantId,
    emailVerified: d.emailVerified ?? true,
  };
}

function loginErrorMessage(e: AxiosError): string {
  const status = e.response?.status ?? 0;
  const body = e.response?.data as { detail?: string; code?: string } | undefined;
  if (status === 401) return "Email/tên đăng nhập hoặc mật khẩu không đúng.";
  if (status === 423 || body?.code === "account_locked") {
    return "Tài khoản tạm khoá do nhập sai nhiều lần. Thử lại sau.";
  }
  if (status === 0) return "Không kết nối được đến máy chủ.";
  return body?.detail ?? "Đăng nhập thất bại. Vui lòng thử lại.";
}
